// @ts-check
// Estado do editor E/OU (filtro avançado das views e condições do público, contrato 09 de público, seção 9): funções
// puras sobre { filters, filterGroups }, um grupo raiz com regras e subgrupos de um nível, cada grupo com E ou OU. O
// rascunho das views (view-state.js) e o construtor do público usam as mesmas funções, então o comportamento é um só.

/**
 * @typedef {import('../../../shared/filter/view-filter-tree.js').ViewFilter} ViewFilter
 * @typedef {{ id: string, parentViewFilterGroupId?: string | null, logicalOperator: string, positionInViewFilterGroup?: number | null }} ViewFilterGroup
 * @typedef {{ filters: ViewFilter[], filterGroups: ViewFilterGroup[] }} FilterTreeState
 */

/**
 * Próxima posição dentro de um grupo (regras e subgrupos dividem a mesma sequência).
 * @param {FilterTreeState} state @param {string | null} groupId
 */
export function nextPositionInGroup(state, groupId) {
  const positions = [
    ...state.filters.filter((filter) => filter.viewFilterGroupId === groupId).map((filter) => filter.positionInViewFilterGroup ?? 0),
    ...state.filterGroups.filter((group) => group.parentViewFilterGroupId === groupId).map((group) => group.positionInViewFilterGroup ?? 0),
  ];
  return positions.length === 0 ? 0 : Math.max(...positions) + 1;
}

/** Grupo raiz, ou undefined. @param {FilterTreeState} state */
export const findRootGroup = (state) => state.filterGroups.find((group) => !group.parentViewFilterGroupId);

/**
 * Regra nova no grupo.
 * @param {FilterTreeState} state @param {string} groupId @param {string} fieldMetadataId @param {string} operand @param {() => string} createId
 * @param {Partial<ViewFilter>} [extra] campos a mais da regra (no público, o campo de destino e o sub-campo)
 * @returns {{ state: FilterTreeState, filter: ViewFilter }}
 */
export function addRule(state, groupId, fieldMetadataId, operand, createId, extra = {}) {
  const filter = { id: createId(), fieldMetadataId, operand, value: '', ...extra, viewFilterGroupId: groupId, positionInViewFilterGroup: nextPositionInGroup(state, groupId) };
  return { state: { ...state, filters: [...state.filters, filter] }, filter };
}

/**
 * Grupo raiz, criado com uma regra quando ainda não existe.
 * @param {FilterTreeState} state @param {string} fieldMetadataId @param {string} operand @param {() => string} createId
 * @returns {{ state: FilterTreeState, root: ViewFilterGroup }}
 */
export function ensureRoot(state, fieldMetadataId, operand, createId) {
  const existing = findRootGroup(state);
  if (existing) return { state, root: existing };
  const root = { id: createId(), parentViewFilterGroupId: null, logicalOperator: 'AND', positionInViewFilterGroup: 0 };
  const withRoot = { ...state, filterGroups: [...state.filterGroups, root] };
  return { state: addRule(withRoot, root.id, fieldMetadataId, operand, createId).state, root };
}

/**
 * Subgrupo do raiz, que nasce com E e uma regra.
 * @param {FilterTreeState} state @param {string} rootId @param {string} fieldMetadataId @param {string} operand @param {() => string} createId
 * @returns {FilterTreeState}
 */
export function addGroup(state, rootId, fieldMetadataId, operand, createId) {
  const group = { id: createId(), parentViewFilterGroupId: rootId, logicalOperator: 'AND', positionInViewFilterGroup: nextPositionInGroup(state, rootId) };
  const withGroup = { ...state, filterGroups: [...state.filterGroups, group] };
  return addRule(withGroup, group.id, fieldMetadataId, operand, createId).state;
}

/**
 * @param {FilterTreeState} state @param {string} filterId @param {Partial<ViewFilter>} changes
 * @returns {FilterTreeState}
 */
export function updateFilter(state, filterId, changes) {
  return { ...state, filters: state.filters.map((filter) => (filter.id === filterId ? { ...filter, ...changes } : filter)) };
}

/**
 * @param {FilterTreeState} state @param {string} groupId @param {'AND' | 'OR'} logicalOperator
 * @returns {FilterTreeState}
 */
export function setGroupOperator(state, groupId, logicalOperator) {
  return { ...state, filterGroups: state.filterGroups.map((group) => (group.id === groupId ? { ...group, logicalOperator } : group)) };
}

/**
 * Tira os grupos que ficaram sem regra nem subgrupo, até não sobrar nenhum vazio.
 * @param {FilterTreeState} state
 * @returns {FilterTreeState}
 */
export function pruneEmptyGroups(state) {
  let groups = state.filterGroups;
  let changed = true;
  while (changed) {
    const current = groups;
    const nonEmpty = current.filter((group) => state.filters.some((filter) => filter.viewFilterGroupId === group.id)
      || current.some((child) => child.parentViewFilterGroupId === group.id));
    changed = nonEmpty.length !== current.length;
    groups = nonEmpty;
  }
  return groups === state.filterGroups ? state : { ...state, filterGroups: groups };
}

/**
 * Remove uma regra e os grupos que ficarem vazios.
 * @param {FilterTreeState} state @param {string} filterId
 * @returns {FilterTreeState}
 */
export function removeRule(state, filterId) {
  return pruneEmptyGroups({ ...state, filters: state.filters.filter((filter) => filter.id !== filterId) });
}

/**
 * Remove o grupo, os subgrupos e as regras deles; o raiz vazio também sai.
 * @param {FilterTreeState} state @param {string} groupId
 * @returns {FilterTreeState}
 */
export function removeGroup(state, groupId) {
  const removedIds = new Set([groupId, ...state.filterGroups.filter((group) => group.parentViewFilterGroupId === groupId).map((group) => group.id)]);
  return pruneEmptyGroups({
    filters: state.filters.filter((filter) => !filter.viewFilterGroupId || !removedIds.has(filter.viewFilterGroupId)),
    filterGroups: state.filterGroups.filter((group) => !removedIds.has(group.id)),
  });
}

/**
 * Itens de um grupo (regras e subgrupos) na ordem de posição.
 * @param {FilterTreeState} state @param {string} groupId
 */
export function groupItems(state, groupId) {
  return [
    ...state.filters.filter((filter) => filter.viewFilterGroupId === groupId).map((filter) => ({ kind: /** @type {const} */ ('rule'), position: filter.positionInViewFilterGroup ?? 0, filter })),
    ...state.filterGroups.filter((group) => group.parentViewFilterGroupId === groupId).map((group) => ({ kind: /** @type {const} */ ('group'), position: group.positionInViewFilterGroup ?? 0, group })),
  ].sort((first, second) => first.position - second.position);
}

/**
 * Aplica uma ação do editor ao estado (o mesmo despacho das views e do público).
 * @param {FilterTreeState} state
 * @param {{ type: string, [key: string]: any }} action
 * @param {() => string} createId
 * @returns {FilterTreeState}
 */
export function applyFilterTreeAction(state, action, createId) {
  switch (action.type) {
    case 'addRule':
      return addRule(state, action.groupId, action.fieldMetadataId, action.operand, createId, action.extra ?? {}).state;
    case 'addGroup':
      return addGroup(state, action.groupId, action.fieldMetadataId, action.operand, createId);
    case 'ensureRoot':
      return ensureRoot(state, action.fieldMetadataId, action.operand, createId).state;
    case 'updateFilter':
      return updateFilter(state, action.filterId, action.changes);
    case 'removeRule':
      return removeRule(state, action.filterId);
    case 'removeGroup':
      return removeGroup(state, action.groupId);
    case 'setGroupOperator':
      return setGroupOperator(state, action.groupId, action.logicalOperator);
    default:
      return state;
  }
}
